/**
 * Upload queue with concurrency control.
 * Prevents saturating the browser with too many parallel uploads.
 */

import { UploadedFile } from './fileUploadService';

export interface UploadProgress {
    phase: 'uploading' | 'extracting' | 'processing';
    current: number;
    total: number;
    fileName: string;
    detail?: string;
}

export interface UploadResult {
    successful: UploadedFile[];
    failed: { file: File; error: Error }[];
}

/**
 * Upload files with limited concurrency.
 * Uses Promise.allSettled so partial failures don't lose successful uploads.
 */
export async function uploadWithConcurrency(
    files: File[],
    uploadFn: (file: File) => Promise<UploadedFile>,
    concurrency: number = 2,
    onProgress?: (progress: UploadProgress) => void,
): Promise<UploadResult> {
    const successful: UploadedFile[] = [];
    const failed: { file: File; error: Error }[] = [];

    // Process in batches of `concurrency`
    for (let i = 0; i < files.length; i += concurrency) {
        const batch = files.slice(i, i + concurrency);

        const results = await Promise.allSettled(
            batch.map(async (file, batchIndex) => {
                const globalIndex = i + batchIndex;
                onProgress?.({
                    phase: 'uploading',
                    current: globalIndex + 1,
                    total: files.length,
                    fileName: file.name,
                    detail: `Subiendo ${globalIndex + 1} de ${files.length}: ${file.name}`,
                });
                return uploadFn(file);
            }),
        );

        results.forEach((result, batchIndex) => {
            const file = batch[batchIndex];
            if (result.status === 'fulfilled') {
                successful.push(result.value);
            } else {
                const error = result.reason instanceof Error
                    ? result.reason
                    : new Error(String(result.reason));
                failed.push({ file, error });
            }
        });
    }

    return { successful, failed };
}

/**
 * Returns a user-friendly error message based on the upload error.
 */
export function getUploadErrorMessage(error: Error, fileName: string): string {
    const msg = error.message?.toLowerCase() || '';

    if (msg.includes('payload too large') || msg.includes('413') || msg.includes('file too large')) {
        return `"${fileName}" es demasiado grande para subir`;
    }
    if (msg.includes('mime') || msg.includes('type') || msg.includes('not allowed') || msg.includes('invalid')) {
        return `"${fileName}" tiene un formato no soportado por el almacenamiento`;
    }
    if (msg.includes('network') || msg.includes('fetch') || msg.includes('timeout') || msg.includes('abort')) {
        return `Error de red al subir "${fileName}". Comprueba tu conexión`;
    }
    if (msg.includes('storage') || msg.includes('bucket') || msg.includes('quota')) {
        return `Error de almacenamiento al subir "${fileName}"`;
    }
    return `Error al subir "${fileName}": ${error.message}`;
}

/**
 * Validates a batch of files before upload.
 * Returns null if valid, or an error message string.
 */
export function validateFilesBatch(
    files: File[],
    existingCount: number = 0,
    maxFiles: number = 10,
    maxSizePerFile: number = 50 * 1024 * 1024, // 50MB
    maxTotalSize: number = 100 * 1024 * 1024, // 100MB
): string | null {
    if (existingCount + files.length > maxFiles) {
        return `Puedes adjuntar un máximo de ${maxFiles} archivos`;
    }

    const allowedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'image/jpeg',
        'image/png',
        'image/webp',
        'audio/wav',
        'audio/x-wav',
        'audio/mpeg',
        'audio/mp4',
        'video/mp4',
        'video/mpeg',
    ];

    const invalidFiles = files.filter(f => !allowedTypes.includes(f.type));
    if (invalidFiles.length > 0) {
        const names = invalidFiles.map(f => f.name).join(', ');
        return `Formato no soportado: ${names}. Se permiten PDF, DOCX, Excel, imágenes, audio y vídeo.`;
    }

    const oversized = files.filter(f => f.size > maxSizePerFile);
    if (oversized.length > 0) {
        return `"${oversized[0].name}" supera el máximo de ${Math.round(maxSizePerFile / (1024 * 1024))}MB por archivo`;
    }

    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > maxTotalSize) {
        return `El tamaño total (${Math.round(totalSize / (1024 * 1024))}MB) supera el máximo de ${Math.round(maxTotalSize / (1024 * 1024))}MB`;
    }

    return null;
}
